import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AskAgent from '../components/AskAgent';
import SeoHead from '../components/SeoHead';
import { caseData } from './Kundecases';
import anonymous from '../assets/anonymousPicture.png';
// background images removed to simplify page background

const CASE_SHIFT = 1;
const CASES_VISIBLE = 3;
const CASE_DUPLICATION = 10; // render list many times to avoid hitting edges
const START_BLOCK = Math.floor(CASE_DUPLICATION / 2); // start roughly in the middle copy
const NARROW_BREAKPOINT = 900;

const services = [
  {
    id: 'consulting',
    badge: 'Rådgivning',
    title: 'AI Discovery & Konsulentbistand',
    description:
      'Opdag hvor AI kan skabe reel værdi i jeres forretning. Vi kortlægger muligheder, gevinster og lavthængende frugter – så I kan arbejde smartere og styrke konkurrencekraften.',
    bullets: [
      'Identifikation af manuelle processer der kan automatiseres',
      'AI-strategi for marketing, rådgivning eller drift',
      'Analyse af jeres data for at finde nye muligheder',
      'Workshops der afdækker AI-potentiale i organisationen',
    ],
  },
  {
    id: 'agents',
    badge: 'Implementering',
    title: 'AI-agenter',
    description:
      'Integrér intelligente AI-agenter i jeres workflow. De overtager tunge, gentagne og tidskrævende opgaver, så teamet kan fokusere på arbejde der skaber værdi.',
    bullets: [
      'Automatiseret rapportering og dataindsamling',
      'AI-agent til kundesupport eller indledende sagsbehandling',
      'Strukturering og kvalitetssikring af vidensmateriale',
      'Automatisering af projektstyring og mødeforberedelse',
    ],
  },
];

const Home = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isNarrow, setIsNarrow] = useState(false);
  const [promptEmail, setPromptEmail] = useState('');
  const [promptSubmitted, setPromptSubmitted] = useState(false);
  const [promptLoading, setPromptLoading] = useState(false);
  // const [webhookStatus, setWebhookStatus] = useState(null); // null | 'ok' | 'error'
  const [caseStartIndex, setCaseStartIndex] = useState(caseData.length * START_BLOCK);
  const casesTrackRef = useRef(null);
  const [cardMetrics, setCardMetrics] = useState({ distance: 0, gap: 0 });
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const slideDistance = cardMetrics.distance;
  const visibleCases = isNarrow ? 1 : CASES_VISIBLE;
  useEffect(() => {
    const updateSlideDistance = () => {
      if (!casesTrackRef.current) return;
      const firstCard = casesTrackRef.current.querySelector('[data-case-card="true"]');
      if (!firstCard) return;
      const { width } = firstCard.getBoundingClientRect();
      const styles = window.getComputedStyle(casesTrackRef.current);
      const gapValue = parseFloat(styles.columnGap || styles.gap || '0');
      setCardMetrics({ distance: width + gapValue, gap: gapValue });
    };

    updateSlideDistance();
    window.addEventListener('resize', updateSlideDistance);
    return () => window.removeEventListener('resize', updateSlideDistance);
  }, []);

  const handleNextCases = () => {
    setCaseStartIndex((prev) => prev + CASE_SHIFT);
  };

  const handlePrevCases = () => {
    setCaseStartIndex((prev) => prev - CASE_SHIFT);
  };

  // Responsive toggle for services layout
  useEffect(() => {
    const checkWidth = () => {
      setIsNarrow(window.innerWidth < NARROW_BREAKPOINT);
    };
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, []);

  const handleOpenCase = (slugOrId) => {
    navigate(`/kundecases/${slugOrId}`);
  };

  const handlePromptSignup = async (e) => {
    e.preventDefault();
    if (!promptEmail) return;
    setPromptLoading(true);
    try {
      await fetch('/api/prompt-signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: promptEmail }),
      });
      setPromptSubmitted(true);
    } catch {
      setPromptSubmitted(true); // still show success to user
    } finally {
      setPromptLoading(false);
    }
  };

  // const handleWebhookTest = async () => {
  //   setWebhookStatus(null);
  //   try {
  //     const res = await fetch('http://localhost:5678/webhook-test/prompt-signup', {
  //       method: 'POST',
  //       headers: { 'Content-Type': 'application/json' },
  //       body: JSON.stringify({ email: 'ch@aice.dk' }),
  //     });
  //     setWebhookStatus(res.ok ? 'ok' : 'error');
  //   } catch {
  //     setWebhookStatus('error');
  //   }
  // };

  // Seamless loop: when we drift too close to the duplicated edges, silently recenter to the middle copy.
  useEffect(() => {
    const baseLength = caseData.length;
    const minIndex = baseLength; // give ourselves a buffer of one full copy
    const maxIndex = baseLength * (CASE_DUPLICATION - 1);

    if (caseStartIndex <= minIndex || caseStartIndex >= maxIndex) {
      setTransitionEnabled(false);
      const normalizedOffset = ((caseStartIndex % baseLength) + baseLength) % baseLength;
      const normalizedIndex = baseLength * START_BLOCK + normalizedOffset;
      requestAnimationFrame(() => {
        setCaseStartIndex(normalizedIndex);
        requestAnimationFrame(() => setTransitionEnabled(true));
      });
    }
  }, [caseStartIndex]);

  const extendedCaseData = Array.from({ length: CASE_DUPLICATION }).flatMap(() => caseData);

  return (
    <div style={pageStyle}>
      <SeoHead
        title="AICE | AI-rådgivning og AI-agenter"
        description="AICE hjælper vidensvirksomheder med AI-strategi, AI-agenter og implementering. Se ydelser, cases og chat direkte fra forsiden."
        canonicalPath="/"
      />
      <main style={mainStyle}>
        <div style={centerWrapper}>
          <h1
            style={{
              ...headingStyle,
              fontSize: isNarrow ? '2.35rem' : headingStyle.fontSize,
              lineHeight: isNarrow ? 1.2 : 1.1,
              paddingTop: isNarrow ? '0.5rem' : headingStyle.paddingTop,
            }}
          >
          AI rådgivning og AI-agenter for din videnstunge virksomhed
          </h1>
          <p style={introText}>Vi hjælper rådgivere, konsulenter og bureauer med at bruge AI strategisk, så I kan effektivisere, innovere og levere endnu mere værdi til jeres kunder.</p>

          <a
            href="https://meetings-eu1.hubspot.com/claus-moeldrup"
            target="_blank"
            rel="noopener noreferrer"
        >
          <button
            style={buttonStyle}
            onMouseOver={(e) => {
              e.target.style.background = 'none';
                e.target.style.color = '#C03626';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'linear-gradient(135deg, #C03626, #FF6F61)';
                e.target.style.color = 'white';
              }}
            >
              Book et møde
            </button>
          </a>

        </div>
        <div style={askAgentWrapper}>
          <AskAgent />
        </div>
        <section style={servicesSection}>
          <p style={servicesEyebrow}>Ydelser</p>
          <h2
            style={{
              ...servicesHeading,
              fontSize: isNarrow ? '2rem' : servicesHeading.fontSize,
              lineHeight: isNarrow ? 1.2 : 1.1,
            }}
          >
            Hvad vi kan hjælpe med
          </h2>
          <p style={servicesIntro}>
            To formater der matcher niveauet i Ydelser-siden: samme varme farver, afrundede kort og tydeligt fokus på udbytte.
            Klik videre for at se hele pakken.
          </p>

          <div
            style={{
              ...servicesGrid,
              gridTemplateColumns: isNarrow ? '1fr' : 'repeat(auto-fit, minmax(280px, 1fr))',
            }}
          >
            {services.map((service) => (
              <a
                key={service.id}
                href="ydelser"
                style={serviceCardStyle}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-6px)';
                  e.currentTarget.style.boxShadow = '0 50px 75px rgba(154, 45, 32, 0.5)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = serviceCardStyle.boxShadow;
                }}
              >
                <span style={serviceBadge}>{service.badge}</span>
                <h3 style={serviceTitle}>{service.title}</h3>
                <p style={serviceDescription}>{service.description}</p>
                <ul style={serviceList}>
                  {service.bullets.map((bullet) => (
                    <li key={bullet} style={serviceListItem}>
                      <span style={serviceListIcon}>✓</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </a>
            ))}
          </div>
        </section>

        <section style={promptLeadSection}>
          <p style={servicesEyebrow}>Ugens prompt for ledere</p>
          <h2 style={promptLeadHeading}>De bedste ledere bruger AI. Her er din ugentlige prompt.</h2>
          <p style={promptLeadSub}>
            Hver uge deler Claus en konkret AI-prompt målrettet ledere i videnstunge virksomheder.
            Find den direkte på LinkedIn.
          </p>

          <a
            href="https://www.linkedin.com/in/clausmoeldrup/recent-activity/all/"
            target="_blank"
            rel="noopener noreferrer"
            style={linkedinCard}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(192, 54, 38, 0.22)';
              e.currentTarget.style.transform = 'translateY(-3px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = linkedinCard.boxShadow;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <span style={linkedinIcon}>in</span>
            <div>
              <p style={linkedinCardTitle}>Find ugens prompt på LinkedIn</p>
              <p style={linkedinCardSub}>Claus Møldrup · Ny prompt hver uge →</p>
            </div>
          </a>

          <div style={promptDivider} />

          {promptSubmitted ? (
            <div style={promptSuccessBox}>
              <p style={{ margin: 0, fontWeight: 600, color: '#2A0D05' }}>Tak for din interesse!</p>
              <p style={{ margin: '0.25rem 0 0', color: '#5C3A32', fontSize: '0.95rem' }}>
                Vi vender tilbage til dig hurtigst muligt.
              </p>
            </div>
          ) : (
            <>
              <p style={promptCta}>
                Vil du have de <strong>10 vigtigste AI-prompts til ledere</strong> i AI-tiden samlet ét sted?
                <br />
                <span style={{ fontWeight: 400, color: '#5C3A32' }}>
                  Tilmeld dig og få dem leveret direkte i din indbakke – helt gratis.
                </span>
              </p>
              <form onSubmit={handlePromptSignup} style={promptForm}>
                <input
                  type="email"
                  required
                  placeholder="din@email.dk"
                  value={promptEmail}
                  onChange={(e) => setPromptEmail(e.target.value)}
                  style={promptInput}
                />
                <button
                  type="submit"
                  disabled={promptLoading}
                  style={promptButton}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'none';
                    e.currentTarget.style.color = '#C03626';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'linear-gradient(135deg, #C03626, #FF6F61)';
                    e.currentTarget.style.color = 'white';
                  }}
                >
                  {promptLoading ? 'Sender...' : 'Send mig de 10 prompts →'}
                </button>
              </form>
              <div style={promptTrustRow}>
                <span style={promptTrustItem}>✓ Gratis</span>
                <span style={promptTrustItem}>✓ Nem at bruge med det samme</span>
                <span style={promptTrustItem}>✓ Ingen spam</span>
              </div>
            </>
          )}
          {/* <div style={{ marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <button
              type="button"
              onClick={handleWebhookTest}
              style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', border: '1px solid #C03626', color: '#C03626', background: '#fff', borderRadius: '6px', cursor: 'pointer' }}
            >
              Test n8n webhook
            </button>
            {webhookStatus === 'ok' && <span style={{ fontSize: '0.8rem', color: '#2e7d32' }}>✓ Webhook modtog requesten</span>}
            {webhookStatus === 'error' && <span style={{ fontSize: '0.8rem', color: '#C03626' }}>✗ Fejl – er n8n kørende?</span>}
          </div> */}
        </section>

        <section style={casesSection}>
          <h2
            style={{
              ...casesHeading,
              fontSize: isNarrow ? '1.7rem' : casesHeading.fontSize,
            }}
          >
            Kundecases
          </h2>
          <div style={casesCarousel}>
            <button
              type="button"
              aria-label="Se tidligere cases"
              onClick={handlePrevCases}
              style={{ ...arrowButton, ...arrowButtonLeft }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#C03626';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#C03626';
              }}
            >
              ‹
            </button>
            <div
              style={{
                ...casesViewport,
                width: isNarrow
                  ? 'clamp(260px, 92vw, 360px)'
                  : slideDistance
                    ? `${Math.max(slideDistance * visibleCases - cardMetrics.gap, 0)}px`
                    : '100%',
              }}
            >
                  <div
                    ref={casesTrackRef}
                    style={{
                      ...casesGrid,
                      transform: `translateX(-${caseStartIndex * slideDistance}px)`,
                      transition: transitionEnabled && slideDistance ? 'transform 0.35s ease-in-out' : 'none',
                    }}
                  >
                {extendedCaseData.map((customerCase, index) => (
                  <div
                    key={`${customerCase.id}-${index}`}
                    style={caseCard}
                    data-case-card="true"
                    onClick={() => handleOpenCase(customerCase.slug || customerCase.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleOpenCase(customerCase.slug || customerCase.id);
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.boxShadow = '0 12px 28px rgba(0, 0, 0, 0)';
                      e.currentTarget.style.transform = 'translateY(-50px)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.boxShadow = '0 6px 18px rgba(0,0,0,0.05)';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  >
                    <div style={caseImage} aria-label={customerCase.title}>
                      {customerCase.image ? (
                        <img
                          src={customerCase.image}
                          alt={customerCase.title}
                          style={caseImageImg}
                          onError={(e) => {
                            if (e.currentTarget.src !== anonymous) e.currentTarget.src = anonymous;
                          }}
                        />
                      ) : (
                        'Billede'
                      )}
                    </div>
                    <h3 style={{ margin: '1rem 0 0.5rem', fontSize: '1.25rem' }}>{customerCase.title}</h3>
                    {customerCase.teaser ? (
                      <p style={caseTeaser}>{customerCase.teaser}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              aria-label="Se næste cases"
              onClick={handleNextCases}
              style={{ ...arrowButton, ...arrowButtonRight }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#C03626';
                e.currentTarget.style.color = '#fff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#fff';
                e.currentTarget.style.color = '#C03626';
              }}
            >
              ›
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default Home;


const buttonStyle = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: 'white',
  padding: '0.5rem 1rem',
  borderRadius: '6px',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  fontSize: '1.02rem',
  padding: '0.75rem 1.5rem',
};

const pageStyle = {
  /* Background images removed — use a simple background color now */
  backgroundColor: '#FFF9F8',
  minHeight: '125vh',
};


const mainStyle = {
  marginTop: '0rem',
};

const centerWrapper = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
};

const introText = {
  marginTop: 0,
  fontSize: '1.25rem',
  fontWeight: '450',
  color: '#111111',
  maxWidth: '900px',
  textAlign: 'center',
};

const meetingLink = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '16px 32px',
  background: '#D73027',
  color: '#ffffff',
  textDecoration: 'none',
  borderRadius: '8px',
  fontSize: '1.1rem',
  fontFamily: 'inherit',
  lineHeight: '1.1',
  marginTop: '1rem',
};

const headingStyle = {
  paddingTop: '1rem',
  fontSize: '3.5rem',
  color: '#111111',
  borderRadius: '8px',
  fontWeight: '600',
  textAlign: 'center',
};

const servicesSection = {
  padding: '70px clamp(1.5rem, 5vw, 4rem)',
  margin: '0 auto',
  maxWidth: '1200px',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  textAlign: 'center',
};

const servicesEyebrow = {
  textTransform: 'uppercase',
  letterSpacing: '0.22em',
  color: '#C03626',
  fontSize: '0.85rem',
  marginBottom: '0.35rem',
};

const servicesHeading = {
  fontSize: '2.5rem',
  margin: 0,
  color: '#2A0D05',
};

const servicesIntro = {
  margin: '0 auto 1rem',
  maxWidth: '720px',
  color: '#5C3A32',
  lineHeight: 1.6,
};

const servicesGrid = {
  display: 'grid',
  gap: '1.5rem',
  width: '100%',
  marginTop: '1rem',
};

const serviceCardStyle = {
  background: '#fff',
  borderRadius: '18px',
  padding: '2rem',
  textDecoration: 'none',
  color: 'inherit',
  border: '1px solid #F3D9D2',
  boxShadow: '0 16px 32px rgba(192, 54, 38, 0.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
};

const serviceBadge = {
  alignSelf: 'flex-start',
  background: '#FFF0EC',
  color: '#C03626',
  borderRadius: '999px',
  padding: '0.25rem 0.9rem',
  fontSize: '0.85rem',
  fontWeight: 600,
};

const serviceTitle = {
  margin: 0,
  fontSize: '1.8rem',
  color: '#2B1610',
};

const serviceDescription = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
};

const serviceList = {
  listStyle: 'none',
  padding: 0,
  margin: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: '0.75rem',
};

const serviceListItem = {
  display: 'flex',
  gap: '0.5rem',
  alignItems: 'flex-start',
  color: '#4A2E25',
  lineHeight: 1.4,
};

const serviceListIcon = {
  color: '#C03626',
  fontWeight: 700,
  marginTop: '0.1rem',
};

const serviceLinkText = {
  marginTop: '0.5rem',
  fontWeight: 600,
  color: '#C03626',
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
};

const askAgentWrapper = {
  marginTop: '1rem',
  padding: '0.25rem',
};

const boxContainer = {
  display: 'flex',
  gap: '4rem',
  justifyContent: 'center',
  flexWrap: 'wrap',
};

const boxStyle = {
  flex: '1 1 300px',
  minWidth: '20%',
  maxWidth: '27%',
  background: 'linear-gradient(to bottom right, rgba(255, 232, 227, 1), rgba(255, 145, 126, 1))',
  borderRadius: '12px',
  padding: '1.2rem',
  paddingTop: '0.5rem',
  boxShadow: '0 4px 10px rgba(0,0,0,0.06)',
  textAlign: 'left',
};

const boxHeading = {
  color: '#C03626',
  marginBottom: '0.75rem',
  fontSize: '1.25rem',
};

const casesSection = {
  padding: '60px 24px',
  margin: '0 auto',
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem',
};

const casesHeading = {
  textAlign: 'center',
  fontSize: '2rem',
  marginBottom: 0,
};

const aboutSection = {
  padding: '40px 24px 80px',
  margin: '0 auto',
  maxWidth: '1000px',
};

const aboutHeading = {
  fontSize: '2.2rem',
  margin: 0,
  color: '#2A0D05',
};

const aboutContent = {
  display: 'flex',
  gap: '2.5rem',
};

const aboutTextBlock = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
};

const aboutText = {
  margin: 0,
  color: '#3C2A25',
  lineHeight: 1.7,
  fontSize: '1.05rem',
};

const aboutButton = {
  alignSelf: 'flex-start',
  padding: '0.7rem 1.6rem',
  borderRadius: '999px',
  border: '1px solid #C03626',
  color: '#C03626',
  fontWeight: 600,
  textDecoration: 'none',
  background: '#fff',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
};

const casesCarousel = {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '90%',
  maxWidth: '1100px',
  margin: '0 auto',
  padding: '0 clamp(1rem, 4vw, 3rem)',
};

const casesViewport = {
  flex: '1 0 auto',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  padding: '3rem 1rem',
};

const casesGrid = {
  display: 'flex',
  flexWrap: 'nowrap',
  gap: '1.5rem',
  justifyContent: 'flex-start',
  width: 'fit-content',
  willChange: 'transform',
};

const arrowButton = {
  width: '48px',
  height: '48px',
  borderRadius: '50%',
  border: '1px solid #f1d5cf',
  background: '#fff',
  color: '#C03626',
  fontSize: '1.5rem',
  cursor: 'pointer',
  boxShadow: '0 6px 12px rgba(0,0,0,0.08)',
  transition: 'background 0.2s ease, color 0.2s ease',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  zIndex: 2,
};

const arrowButtonLeft = {
  left: '-1rem',
};

const arrowButtonRight = {
  right: '-1rem',
};

const caseCard = {
  flex: '0 0 clamp(240px, 28vw, 320px)',
  width: 'clamp(240px, 28vw, 320px)',
  maxWidth: '320px',
  background: '#fff',
  borderRadius: '12px',
  border: '1px solid #f1d5cf',
  padding: '1.5rem',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: '0.75rem',
  boxShadow: '0 6px 18px rgba(0,0,0,0.05)',
  cursor: 'pointer',
  transition: 'box-shadow 0.2s ease, transform 0.2s ease'
};

const caseImage = {
  width: 'calc(100% + 3rem)',
  height: '160px',
  background: '#FDF0EC',
  borderRadius: '12px',
  marginLeft: '-1.5rem',
  marginRight: '-1.5rem',
  marginTop: '-1.5rem',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#C03626',
  fontWeight: 600,
  overflow: 'hidden',
};

const caseImageImg = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
};

const caseTeaser = {
  color: '#4a4a4a',
  margin: 0,
  minHeight: '48px',
  lineHeight: 1.5,
};

const caseLogoImg = {
  width: '100%',
  height: '100%',
  objectFit: 'contain',
};

const promptLeadSection = {
  padding: '70px clamp(1.5rem, 5vw, 4rem)',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '1.25rem',
  textAlign: 'center',
  boxSizing: 'border-box',
};

const promptLeadHeading = {
  fontSize: '2rem',
  margin: 0,
  color: '#2A0D05',
  lineHeight: 1.2,
};

const promptLeadSub = {
  margin: 0,
  color: '#5C3A32',
  lineHeight: 1.6,
  maxWidth: '560px',
};

const linkedinCard = {
  display: 'flex',
  alignItems: 'center',
  gap: '1rem',
  background: '#fff',
  border: '1px solid #F3D9D2',
  borderRadius: '14px',
  padding: '1rem 1.5rem',
  textDecoration: 'none',
  color: 'inherit',
  boxShadow: '0 6px 18px rgba(192, 54, 38, 0.08)',
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  width: '100%',
  maxWidth: '480px',
};

const linkedinIcon = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '44px',
  height: '44px',
  minWidth: '44px',
  background: '#0A66C2',
  color: '#fff',
  borderRadius: '10px',
  fontWeight: 700,
  fontSize: '1.1rem',
};

const linkedinCardTitle = {
  margin: 0,
  fontWeight: 600,
  color: '#2A0D05',
  fontSize: '1rem',
};

const linkedinCardSub = {
  margin: '0.2rem 0 0',
  color: '#C03626',
  fontSize: '0.88rem',
};

const promptDivider = {
  width: '40px',
  height: '2px',
  background: '#F3D9D2',
  borderRadius: '999px',
};

const promptCta = {
  margin: 0,
  fontSize: '1.1rem',
  color: '#2A0D05',
  lineHeight: 1.6,
  maxWidth: '540px',
};

const promptForm = {
  display: 'flex',
  gap: '0.75rem',
  width: '100%',
  maxWidth: '520px',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const promptInput = {
  flex: '1 1 220px',
  padding: '0.75rem 1rem',
  borderRadius: '8px',
  border: '1px solid #F3D9D2',
  fontSize: '1rem',
  outline: 'none',
  background: '#fff',
  color: '#2A0D05',
};

const promptButton = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: 'white',
  padding: '0.75rem 1.4rem',
  borderRadius: '8px',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '1rem',
  transition: 'all 0.3s ease',
  whiteSpace: 'nowrap',
};

const promptTrustRow = {
  display: 'flex',
  gap: '1.5rem',
  flexWrap: 'wrap',
  justifyContent: 'center',
};

const promptTrustItem = {
  color: '#5C3A32',
  fontSize: '0.88rem',
};

const promptSuccessBox = {
  background: '#FFF0EC',
  border: '1px solid #F3D9D2',
  borderRadius: '12px',
  padding: '1.25rem 2rem',
  textAlign: 'center',
};

const boxContents = {
  color: '#333',
  lineHeight: '1.5',
  fontSize: '1.1rem',
};

const bookSection = {
  textAlign: 'center',
  marginTop: '1rem',
};

const bookText = {
  paddingTop: '1rem',
  fontSize: '2rem',
  color: '#111111',
  padding: '1rem',
  borderRadius: '8px',
  fontWeight: '600',
  textAlign: 'center',
};

const bookButton = {
  background: 'linear-gradient(135deg, #C03626, #FF6F61)',
  color: '#fff',
  padding: '0.75rem 1.25rem',
  borderRadius: '6px',
  border: 'none',
  fontWeight: 'bold',
  cursor: 'pointer',
  fontSize: '1.5rem',
  transition: 'all 0.3s ease',
};
